import {SlctInternalProps, SlctOption, SlctProps} from 'component/Slct/SlctType'
import React, {ChangeEventHandler, FocusEventHandler, PropsWithChildren, useCallback, useEffect, useMemo, useRef, useState} from 'react'
import {css} from '@emotion/react'
import {CircularProgress, Portal} from '@mui/material'
import {SlctOptionComponent} from 'component/Slct/SlctOptionComponent'
import {FixedSizeList, ListChildComponentProps} from 'react-window'
import {log} from 'common/log'
import _ from 'lodash'
import {getOptionLabel} from 'component/Slct/getOptionLabel'
import {Key} from 'util/Key'
import {Clear} from '@mui/icons-material'

const cssWrapper = css`
	width: 100%;
	box-sizing: border-box;
	height: 23px;
	background: #fff;
	border: 1px solid #ccc;
	border-radius: 3px;
	display: grid;
	grid-template-columns: max-content min-content min-content;
	align-items: center;
`
const cssChildBase = css`
	border-radius: 3px;
	background: none;
`
const cssInput = css`
	grid-area: 1 / 1 / span 1 / span 1;
	border: none;
	outline: none;
	border-radius: 3px;
	background: none;
`
const cssPlaceholder = css`
	grid-area: 1 / 1 / span 1 / span 1;
	border-radius: 3px;
	background: none;
	padding: 0 4px;
`
const cssInputLoadingWrapper = css`
	grid-area: 1 / 2 / span 1 / span 1;
`
const cssClearButton = css`
	grid-area: 1 / 3 / span 1 / span 1;
	border-radius: 30px;
	display: inline-block;
	height: 20px;
	width: 20px;
	color: #a5a5a5;

	&:hover {
		color: #b6b6b6;
		background: #dddddd;
	}
`
const cssPlaceholderText = css`
	white-space: nowrap;
	overflow: hidden;
	text-overflow: ellipsis;
`
const cssMenu = css`
	background: #fff;
	border: 1px solid #666;
	position: fixed;
	overflow: auto;
	border-radius: 3px;
`
const cssMenuLoadingWrapper = css`
	height: 100%;
	width: 100%;
	box-sizing: border-box;
	display: flex;
	align-items: center;
	justify-content: center;
`
const DEFAULT_WIDTH = 300;
const ITEM_SIZE = 28;

const Row = React.memo(function Row<Option extends SlctOption>(props: PropsWithChildren<ListChildComponentProps<SlctInternalProps<Option>>>) {
	const {data, index, style} = props
	const option = data.options[index]
	return <div style={style} onClick={() => data.onChange(option)}>
		<SlctOptionComponent index={index} selectProps={data} option={option}/>
	</div>
})

/* use memo functions to pass props to this component */
export const Slct = React.memo(function SelectComponent<Option extends SlctOption>(props: SlctProps<Option>) {
	const {
		options, value, onChange: _onChange, filterOption,
		displayFormat, showClearButton,
		menuIsOpen, setMenuIsOpen, preventCloseMenuOnSelect,
		disabled, loading,
		placeholder = '', autoFocus, tabIndex = 0,
		mustMatch,
	} = props
	const maxIndex = options.length - 1
	const ref = useRef<HTMLDivElement>(null)
	const refInput = useRef<HTMLInputElement>(null)
	const refMenu = useRef<HTMLDivElement>(null)
	const refList = useRef<FixedSizeList>(null)
	const [inputValue, setInputValue] = useState('')
	const [cursor, setCursor] = useState<number | null>(null)
	const [focus, setFocus] = useState(false)
	let [menuOpen, setMenuOpen] = useState(menuIsOpen || false)
	if (typeof menuIsOpen === 'boolean' && typeof setMenuIsOpen === 'function') {
		menuOpen = menuIsOpen
		setMenuOpen = setMenuIsOpen
	}

	const onInputChange = useCallback<ChangeEventHandler<HTMLInputElement>>((evt) => {
		log.trace('Slct onInputChange', evt)
		setInputValue(evt.target.value || '')
	}, [refInput])

	const onInputFocus = useCallback((evt) => {
		log.trace('Slct onInputFocus', evt, focus, menuOpen)
		if (!focus) {
			//first time only
			setInputValue(!value && value !== 0 ? '' : value + '')
			setMenuOpen(true)
			setFocus(true)
			setTimeout(() => refInput.current?.select(), 2)
		}
	}, [refInput, focus, value])

	const onInputBlur: FocusEventHandler<HTMLElement> = useCallback((evt) => {
		const focusedMenu = refMenu.current?.contains(evt.relatedTarget)
		const focusedInput = refInput.current?.contains(evt.relatedTarget)
		if (focusedMenu || focusedInput) {
			log.trace('Slct onInputBlur cancel', evt)
			setMenuOpen(true)
			setFocus(true)
			evt.preventDefault()
			refInput.current?.focus()
			return;
		}
		log.trace('Slct onInputBlur blur', evt)
		// clicked / tabbed outside
		setMenuOpen(false)
		setCursor(null)
		setFocus(false)
		const optionFound = options.find(x => x.value === inputValue)
		if (
			mustMatch ?
				!inputValue || inputValue !== value && optionFound
				:
				!inputValue && ((value || value === 0 ? value : '') + '')?.trim()
		) {
			_onChange(inputValue && (inputValue + '').trim() ? {value: inputValue, label: inputValue} as Option : null)
			setInputValue('')
		}
	}, [refInput, refMenu, value, inputValue, _onChange, options])

	const onClear = useCallback(() => {
		_onChange(null)
		setInputValue('')
	}, [_onChange])

	const onKeyUp = useCallback((evt) => {
		log.trace('Slct onKeyUp', evt)
		setTimeout(() => {
			if (window.document.activeElement === refInput.current) setMenuOpen(true)
		}, 200);
	}, [refInput])

	const onKeyDown = useCallback((evt) => {
		log.trace('Slct onKeyDown', evt)
		if (evt.key === Key.Tab && cursor !== null) {
			const item = options[cursor]
			if (item) {
				_onChange(item)
				setInputValue((item.value || '') + '')
			}
			return;
		}
		let move = 0
		if (evt.key === Key.Up) {
			move = -1
		} else if (evt.key === Key.Down) {
			move = 1
		} else {
			// search, delete or any other input should remove the cursor to prevent input of a wrong value
			setCursor(null)
			return;
		}
		let next = cursor === null ? 0 : cursor + move
		if (next < 0) next = maxIndex
		else if (next > maxIndex) next = 0
		setCursor(next)
	}, [maxIndex, cursor])

	const itemData: SlctInternalProps<Option> = {
		...props,
		inputValue,
		focus,
		cursor, setCursor,
		onChange: useCallback((o) => {
				log.trace('Slct onChange', o)
				_onChange(o)
				setInputValue((o?.value || '') + '')
				if (!preventCloseMenuOnSelect) {
					setMenuOpen(false)
					setCursor(null)
				}
				setTimeout(() => {
					if (refInput.current && refInput.current === window.document.activeElement) refInput.current.select()
				}, 20)
			}, [_onChange, refInput, window],
		),
	}

	const valueOption = useMemo(() => options.find(v => v.value === value), [options, value])
	const displayValue = getOptionLabel(displayFormat, value, valueOption?.label)

	//style and position
	const [boundingRect, setBoundingRect] = useState<Partial<DOMRect>>(ref.current?.getBoundingClientRect() || {})
	useEffect(() => {
		if (!menuOpen) return;
		const handleResizeForSlct = _.debounce(function handleResizeForSlct<K extends keyof WindowEventMap>(evt?: WindowEventMap[K]) {
			const boundingRect = ref.current?.getBoundingClientRect() || {}
			log.trace('slct resize callback', evt, boundingRect)
			setBoundingRect(boundingRect)
		}, 10)
		const panelWrapContent = window.document.getElementById('PanelViewWrapContent')
		window.addEventListener('resize', handleResizeForSlct)
		panelWrapContent?.addEventListener('scroll', handleResizeForSlct)
		handleResizeForSlct()
		return () => {
			window.removeEventListener('resize', handleResizeForSlct)
			panelWrapContent?.removeEventListener('scroll', handleResizeForSlct)
		}
	}, [ref, window, menuOpen])

	useEffect(() => refList.current?.scrollToItem(cursor || 0, "auto"), [refList, cursor])

	const optionsLen = options.length

	let topPos = ((boundingRect.top || 0) + (boundingRect.height || 0)) + 4
	const leftPos = (boundingRect.left || 0)
	const width = (boundingRect.width || DEFAULT_WIDTH)
	const availableHeight = window.innerHeight - topPos
	const menuHeight = Math.min(ITEM_SIZE * optionsLen, availableHeight, 350)
	if (topPos + menuHeight > window.innerHeight) {
		topPos = window.innerHeight - menuHeight - 1
	}
	log.debug(topPos, leftPos, width, availableHeight, menuHeight, 'bounding:', boundingRect.top, boundingRect.height)

	return <>
		<div
			className={'SlctWrapper'}
			css={cssWrapper}
			tabIndex={tabIndex}
			onFocus={onInputFocus}
			ref={ref}
		>
			<div
				className={'SlctPlaceholder'}
				css={[cssChildBase, cssPlaceholder]}
				style={{width: width - 8, opacity: !value && value !== 0 ? 0.5 : value}}
			>
				{!focus && <div css={cssPlaceholderText} title={displayValue}>
					{!value && value !== 0 ? placeholder : displayValue}
				</div>}
			</div>
			<input
				className={'SlctInput'}
				css={[cssChildBase, cssInput]}
				ref={refInput}
				autoFocus={autoFocus}
				value={focus ? inputValue : ''}
				onChange={onInputChange}
				onBlur={onInputBlur}
				onKeyUp={onKeyUp}
				onKeyDown={onKeyDown}
				disabled={disabled}
			/>
			{showClearButton && value &&
				<div
					css={cssClearButton}
					style={{left: width - 24}}
					onClick={onClear}
				>
					<Clear fontSize={'small'}/>
				</div>
			}
		</div>
		<Portal container={document.body}>
			{menuOpen &&
				<div
					ref={refMenu}
					tabIndex={tabIndex + 1}
					className={'SlctMenu'}
					css={[
						cssMenu,
						css`
							top: ${topPos}px;
							left: ${leftPos}px;
							width: ${width}px;
							height: ${menuHeight}px;
						`,
					]}
				>
					{loading &&
						<div css={cssMenuLoadingWrapper}><CircularProgress/></div>
						||
						<FixedSizeList
							ref={refList}
							itemData={itemData}
							itemCount={optionsLen}
							itemSize={ITEM_SIZE}
							height={menuHeight}
							width={width}
						>
							{Row}
						</FixedSizeList>
					}
				</div>
			}
		</Portal>
	</>
})